import { useState, useRef, useCallback } from "react";
import { authFetch } from "../App";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CalendarPlus, Mic, MicOff, X, Loader2, Clock, FolderOpen,
} from "lucide-react";
import { Button } from "./ui/button";

const API = "/api";

export function QuickAppointmentButtons({ onCreated }) {
  const [showModal, setShowModal] = useState(false);
  const [voiceState, setVoiceState] = useState("idle"); // idle | recording | processing
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const queryClient = useQueryClient();

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["upcoming-appointments"] });
    onCreated?.();
  }, [queryClient, onCreated]);

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 100) {
          toast.error("Keine Aufnahme erkannt");
          setVoiceState("idle");
          return;
        }
        setVoiceState("processing");
        try {
          const fd = new FormData();
          fd.append("audio", blob, "rec.webm");
          const res = await authFetch(`${API}/appointments/voice`, {
            method: "POST",
            body: fd,
          });
          if (!res.ok) {
            toast.error("Fehler bei Spracherkennung");
            setVoiceState("idle");
            return;
          }
          const data = await res.json();
          toast.success(`Erkannt: "${data.transcript}"`);
          const payload = {
            title: data.title || data.transcript,
            date: data.date || format(new Date(), "yyyy-MM-dd"),
            time: data.time || null,
            end_time: data.end_time || null,
            project_id: data.project_id || "",
            project_name: data.project_name || "",
            participants: data.participants || [],
            location: data.location || "",
            note: data.note || "",
            visibility: "privat",
            is_quick: data.is_quick || !data.time,
          };
          const cr = await authFetch(`${API}/appointments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (cr.ok) {
            toast.success("Termin erstellt");
            refresh();
          }
        } catch {
          toast.error("Fehler");
        }
        setVoiceState("idle");
      };
      mediaRec.start();
      mediaRecRef.current = mediaRec;
      setVoiceState("recording");
    } catch {
      toast.error("Mikrofon nicht verfuegbar");
    }
  };

  const stopVoice = () => {
    if (mediaRecRef.current?.state !== "inactive") mediaRecRef.current?.stop();
    setVoiceState("idle");
  };

  return (
    <>
      <div className="flex items-center gap-1.5" data-testid="quick-appointment-buttons">
        <Button
          size="sm"
          onClick={() => setShowModal(true)}
          className="gap-1.5 h-7 text-[11px] bg-slate-800 hover:bg-slate-900"
          data-testid="quick-appointment-btn"
        >
          <CalendarPlus className="w-3.5 h-3.5" />
          Termin
        </Button>

        {voiceState === "idle" && (
          <Button
            size="sm"
            onClick={startVoice}
            className="gap-1.5 h-7 text-[11px] bg-purple-600 hover:bg-purple-700"
            data-testid="quick-voice-btn"
          >
            <Mic className="w-3.5 h-3.5" />
            Sprechen
          </Button>
        )}
        {voiceState === "recording" && (
          <Button
            size="sm"
            onClick={stopVoice}
            className="gap-1.5 h-7 text-[11px] bg-red-500 hover:bg-red-600 animate-pulse"
            data-testid="quick-voice-stop-btn"
          >
            <MicOff className="w-3.5 h-3.5" />
            Stopp
          </Button>
        )}
        {voiceState === "processing" && (
          <Button
            size="sm"
            disabled
            className="gap-1.5 h-7 text-[11px] bg-purple-100 text-purple-700"
          >
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            KI...
          </Button>
        )}
      </div>

      {showModal && (
        <QuickCreateModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

function QuickCreateModal({ onClose, onCreated }) {
  const today = format(new Date(), "yyyy-MM-dd");
  const now = format(new Date(), "HH:mm");
  const [form, setForm] = useState({
    title: "",
    date: today,
    time: now,
    end_time: "",
    project_id: "",
  });
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadedProjects, setLoadedProjects] = useState(false);

  // Load projects on mount
  if (!loadedProjects) {
    setLoadedProjects(true);
    authFetch(`${API}/projects`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setProjects(Array.isArray(data) ? data : data.projects || []))
      .catch(() => {});
  }

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Titel fehlt");
      return;
    }
    setSaving(true);
    try {
      const proj = projects.find((p) => p.id === form.project_id);
      const payload = {
        title: form.title.trim(),
        date: form.date,
        time: form.time || null,
        end_time: form.end_time || null,
        project_id: form.project_id || "",
        project_name: proj?.name || "",
        participants: [],
        location: "",
        note: "",
        visibility: "privat",
        is_quick: false,
      };
      const res = await authFetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Termin erstellt");
        onCreated();
      } else {
        const e = await res.json();
        toast.error(e.detail || "Fehler");
      }
    } catch {
      toast.error("Fehler");
    }
    setSaving(false);
  };

  const inp =
    "w-full px-3 py-2.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 bg-white transition-shadow";

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quick-create-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-create-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CalendarPlus className="w-4 h-4 text-slate-600" />
            <h2 className="text-[14px] font-semibold text-gray-900">
              Schnelltermin
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
            data-testid="quick-create-close"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-3">
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Titel *"
            className={inp}
            autoFocus
            data-testid="quick-title"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />

          <div className="grid grid-cols-3 gap-2">
            <div className="relative">
              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, date: e.target.value }))
                }
                className={inp}
                data-testid="quick-date"
              />
            </div>
            <div className="relative">
              <input
                type="time"
                value={form.time}
                onChange={(e) =>
                  setForm((f) => ({ ...f, time: e.target.value }))
                }
                className={inp}
                data-testid="quick-time"
              />
            </div>
            <div className="relative">
              <input
                type="time"
                value={form.end_time}
                onChange={(e) =>
                  setForm((f) => ({ ...f, end_time: e.target.value }))
                }
                className={inp}
                placeholder="Ende"
                data-testid="quick-end-time"
              />
            </div>
          </div>

          <select
            value={form.project_id}
            onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))}
            className={inp}
            data-testid="quick-project"
          >
            <option value="">Projekt (optional)</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            Abbrechen
          </button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-1.5 text-[13px] bg-slate-800 hover:bg-slate-900"
            data-testid="quick-save-btn"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Erstellen
          </Button>
        </div>
      </div>
    </div>
  );
}

// Mobile Floating Action Button
export function MobileQuickAppointmentFAB() {
  const [showModal, setShowModal] = useState(false);
  const [voiceState, setVoiceState] = useState("idle");
  const [expanded, setExpanded] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const queryClient = useQueryClient();

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["upcoming-appointments"] });
  }, [queryClient]);

  const startVoice = async () => {
    setExpanded(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 100) {
          toast.error("Keine Aufnahme");
          setVoiceState("idle");
          return;
        }
        setVoiceState("processing");
        try {
          const fd = new FormData();
          fd.append("audio", blob, "rec.webm");
          const res = await authFetch(`${API}/appointments/voice`, {
            method: "POST",
            body: fd,
          });
          if (!res.ok) {
            toast.error("Fehler");
            setVoiceState("idle");
            return;
          }
          const data = await res.json();
          toast.success(`Erkannt: "${data.transcript}"`);
          const payload = {
            title: data.title || data.transcript,
            date: data.date || format(new Date(), "yyyy-MM-dd"),
            time: data.time || null,
            end_time: data.end_time || null,
            project_id: data.project_id || "",
            project_name: data.project_name || "",
            participants: data.participants || [],
            location: data.location || "",
            note: data.note || "",
            visibility: "privat",
            is_quick: data.is_quick || !data.time,
          };
          const cr = await authFetch(`${API}/appointments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (cr.ok) {
            toast.success("Termin erstellt");
            refresh();
          }
        } catch {
          toast.error("Fehler");
        }
        setVoiceState("idle");
      };
      mediaRec.start();
      mediaRecRef.current = mediaRec;
      setVoiceState("recording");
    } catch {
      toast.error("Mikrofon nicht verfuegbar");
    }
  };

  const stopVoice = () => {
    if (mediaRecRef.current?.state !== "inactive") mediaRecRef.current?.stop();
    setVoiceState("idle");
  };

  return (
    <>
      {/* Backdrop when expanded */}
      {expanded && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setExpanded(false)}
        />
      )}

      <div className="fixed bottom-20 right-4 z-50 flex flex-col items-end gap-2" data-testid="mobile-quick-fab">
        {/* Expanded options */}
        {expanded && (
          <div className="flex flex-col gap-2 animate-in slide-in-from-bottom-2 fade-in duration-200">
            <button
              onClick={() => {
                setExpanded(false);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-lg border border-gray-200 text-[13px] font-medium text-gray-800 active:scale-95 transition-transform"
              data-testid="mobile-fab-new-appointment"
            >
              <CalendarPlus className="w-4 h-4 text-slate-600" />
              Neuer Termin
            </button>
            <button
              onClick={startVoice}
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 rounded-xl shadow-lg text-[13px] font-medium text-white active:scale-95 transition-transform"
              data-testid="mobile-fab-voice"
            >
              <Mic className="w-4 h-4" />
              Termin sprechen
            </button>
          </div>
        )}

        {/* Main FAB */}
        {voiceState === "idle" && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-14 h-14 rounded-full bg-slate-800 text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform"
            data-testid="mobile-fab-main"
          >
            {expanded ? (
              <X className="w-6 h-6" />
            ) : (
              <CalendarPlus className="w-6 h-6" />
            )}
          </button>
        )}

        {voiceState === "recording" && (
          <button
            onClick={stopVoice}
            className="w-14 h-14 rounded-full bg-red-500 text-white shadow-xl flex items-center justify-center animate-pulse"
            data-testid="mobile-fab-voice-stop"
          >
            <MicOff className="w-6 h-6" />
          </button>
        )}

        {voiceState === "processing" && (
          <div className="w-14 h-14 rounded-full bg-purple-100 shadow-xl flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
        )}
      </div>

      {showModal && (
        <QuickCreateModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            refresh();
          }}
        />
      )}
    </>
  );
}
